import React from "react";
import { MapStateToPropsParam } from "react-redux";

import blogsSharedSaga from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.sagas";
import commentsSaga from "@/mk2/apps/blogs/containers/Comments/Comments.sagas";
import {
    getCommentIdsOrNull,
    getPostsState,
} from "@/mk2/apps/blogs/containers/Comments/Comments.selectors";
import {
    BLOG_POST_MENU_MORE,
    BLOG_POST_VISIBILITY_MENU_MORE,
    getPostModel,
} from "@/mk2/apps/blogs/helpers";
import { shortMessageBlogsOG } from "@/mk2/apps/blogs/structuredData";
import { blogsUrl } from "@/mk2/apps/blogs/urls";
import { getCommentComplaintsCounts } from "@/mk2/apps/groups/containers/GroupShared/GroupShared.selectors";
import { AppName } from "@/mk2/containers/Application/Application";
import { complaintOpenTrigger } from "@/mk2/containers/Complaint/Complaint.actions";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { getComplaintShownForBlogPost } from "@/mk2/containers/Complaints/Complaints.selectors";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    HalfpageProps,
    InterstitialProps,
    PageRouteProps,
    RightContentSide,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    BlogPostEntity,
    BlogPostSchema,
    MenuSelection,
    PostVisibility,
    UserEntity,
} from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import { SEO_TITLE_BLOGS_SHORT_MESSAGE_OF_username } from "@/mk/autogenerated/translation";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import {
    blogsDeletePostTrigger,
    blogsIgnoreUserTrigger,
    blogsSetDisplayingAtFeedTrigger,
    blogsSetPostVisibilityTrigger,
} from "../BlogsShared/BlogsShared.actions";
import { BlogsShortMessageDetail } from "./BlogsShortMessageDetail";
import { blogsShortMessageDetailFetchTrigger } from "./BlogsShortMessageDetail.actions";
import { blogsShortMessageDetailReducer } from "./BlogsShortMessageDetail.reducers";
import blogsShortMessageDetailSaga from "./BlogsShortMessageDetail.sagas";
import { getBlogsShortMessageDetailState } from "./BlogsShortMessageDetail.selectors";

interface OwnProps {}

interface StateProps {
    baseUrl: string;
    commentsCount: number;
    complaintsCount: number;
    isMobile: boolean;
    loadingState: LoadingState;
    errorMessage: string;
    post: BlogPostEntity;
    postComplaintOpened: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
}

interface DispatchProps {
    onDelete(postId: number, redirectUrl?: string);
    onIgnoreUser(username: string);
    onLoad(username: string, postSlug: string, accessKey: string);
    onPostToggleMenuMore(postId: number);
    onPostToggleVisibilityMenuMore(postId: number);
    onReport(entity: ComplaintTargetType, id: number, location: HistoryLocationWithState);
    onSetDisplayingAtFeed(postId: number, displaying: boolean);
    onSetVisibility(postId: number, visibility: PostVisibility);
}

type RouteProps = PageRouteProps<{
    username: string;
    postSlug: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class BlogsShortMessageDetailPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                routeParams: { username, postSlug },
            } = ownProps;

            const pageState = getBlogsShortMessageDetailState(state, username, postSlug);
            const post: BlogPostEntity = getDenormalizedEntity(
                state,
                BlogPostSchema,
                pageState.postId,
            );

            const requestUserPermissions = getRequestPermissions(state);
            const isAdmin = requestUserPermissions.indexOf("photoblog.can_manage_blogs") >= 0;

            let commentsCount = 0;
            if (post) {
                const key = `${getPostModel(post)}/${post.id}`;
                const commentIdsOrNull = getCommentIdsOrNull(getPostsState(state), key);
                commentsCount = commentIdsOrNull ? commentIdsOrNull.length : post.commentsCount;
            }

            const complaintsCount =
                isAdmin && post
                    ? getCommentComplaintsCounts(state, [post.id], LoadingState.SUCCESS)[post.id]
                    : 0;

            return {
                baseUrl: getRequestBaseUrl(state),
                commentsCount,
                complaintsCount,
                isMobile: getRequestDeviceMobile(state),
                loadingState: pageState.loadingState,
                errorMessage: pageState.errorMessage,
                post,
                postComplaintOpened: getComplaintShownForBlogPost(state, post),
                requestUser: getRequestUser(state),
                requestUserPermissions,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onDelete: blogsDeletePostTrigger,
            onIgnoreUser: blogsIgnoreUserTrigger,
            onLoad: blogsShortMessageDetailFetchTrigger,
            onPostToggleMenuMore: (postId: number) => menuToggle(BLOG_POST_MENU_MORE(postId)),
            onPostToggleVisibilityMenuMore: (postId: number) =>
                menuToggle(BLOG_POST_VISIBILITY_MENU_MORE(postId)),
            onReport: complaintOpenTrigger,
            onSetDisplayingAtFeed: blogsSetDisplayingAtFeedTrigger,
            onSetVisibility: blogsSetPostVisibilityTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const {
            post,
            routeParams: { username },
        } = props;
        // Use perex content as title if available, otherwise fall back to default
        if (post?.perexAST) {
            const perexText = getFirstParagraphsPlaintext(post.perexAST);
            if (perexText) {
                const prefix = `@${username}: `;
                const maxPerexLength = 120 - prefix.length;
                const truncatedPerex =
                    perexText.length > maxPerexLength
                        ? perexText.substring(0, maxPerexLength - 3) + "..."
                        : perexText;
                return prefix + truncatedPerex;
            }
        }
        return interpolate(SEO_TITLE_BLOGS_SHORT_MESSAGE_OF_username, { username });
    }

    public getMetaDescription(props: Props): string {
        const { post } = props;
        if (post?.perexAST) {
            return getFirstParagraphsPlaintext(post.perexAST);
        }
        return null;
    }

    public getOpenGraphData(
        props: Props,
        baseUrl: string,
        currentUrl: string,
    ): { [key: string]: string } {
        const { post } = props;
        if (post) {
            return {
                ...super.getOpenGraphData(props, baseUrl, currentUrl),
                ...shortMessageBlogsOG(baseUrl, post),
            };
        }
    }

    public render(props: Props): false | JSX.Element {
        return <BlogsShortMessageDetail {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return blogsUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Blogs Feed - Halfpage Sticky (Desktop)",
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: props.isMobile ? "Blogs Post - Top 01 - Mobile" : "Blogs - Branding",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: props.isMobile ? "Blogs Post - Interstitial" : "Blogs - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { commentsCount, requestUser, isMobile, post } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if (!post || (commentsCount || 0) < 8) {
            return null;
        }

        return {
            zone: "Blogs Post - Anchor - Mobile",
        };
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: "photoblog",
            activeItem: null,
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer(
            "containers.blogs.blogsShortMessageDetails",
            blogsShortMessageDetailReducer,
        );
        store.injectSaga("containers.blogs.blogsShortMessageDetails", blogsShortMessageDetailSaga);

        store.injectSaga("containers.blogs.blogsShared", blogsSharedSaga);

        store.injectSaga("containers.blogs.shared.comments", commentsSaga);
    }
}
